<template>
  <div>
    <ListingLayout
      :is-loading="intervalLoading ? false : isLoading"
      show-date
      :meta="meta"
      :headers="_headers"
      :items="list"
      selected-product="cso"
      :product-options="productOptions"
      :filter-names="['location_city', 'locations', 'charge_point']"
      search-label="search"
      :export-options="['excel', 'csv']"
      :vertical-scroll="false"
      @isExport="downloadFiles"
      @filterOption="onFiltersChange"
    >
      <template v-slot:sno="{ index }">
        <div>
          {{ (meta.page - 1) * meta.itemsPerPage + index + 1 }}
        </div>
      </template>

      <template v-slot:id="{ item }">
        <div
          class="open-link"
          @click="openDetailModal(item)"
        >
          <span>{{ item.id }}</span>
        </div>
      </template>

      <template v-slot:charge_point_id="{ item }">
        <div>
          <span>{{ item.charge_point_id }}/{{ item.connector_id }}</span>
        </div>
      </template>

      <template v-slot:location_name="{ item }">
        <div>
          <div>{{ item?.location_display_name }}</div>
          <div>{{ item?.location_name }}</div>
        </div>
      </template>

      <template v-slot:date="{ item }">
        <div>{{ item.started_at | formatDate("DD-MM-YYYY") }}</div>
      </template>
      <template v-slot:started_at="{ item }">
        <div>{{ item.started_at | formatTime }}</div>
      </template>

      <template v-slot:start_soc="{ item }">
        <div>
          <span v-if="item.start_soc || item.start_soc == '0'">
            {{ item.start_soc }}
          </span>
          <span v-else>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  N/A
                </span>
              </template>
              <span>Feature not supported in this charger model</span>
            </v-tooltip>
          </span>
        </div>
      </template>

      <template v-slot:completed_at="{ item }">
        <div>{{ item.completed_at | formatTime }}</div>
      </template>

      <template v-slot:end_soc="{ item }">
        <div>
          <span v-if="item.end_soc || item.end_soc == '0'">
            {{ item.end_soc }}
          </span>
          <span v-else>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  N/A
                </span>
              </template>
              <span>Feature not supported in this charger model</span>
            </v-tooltip>
          </span>
        </div>
      </template>

      <template v-slot:customer_name="{ item }">
        <span>
          <!-- v-if="item.walk_in_customer_type == 1" -->
          <div
            class="open-link"
            @click="editItem(item)"
          >
            {{ item.customer_name }}
          </div>
          <!-- <div v-else>
            {{ item.customer_name }}
          </div> -->
        </span>
      </template>
      <template v-slot:duration="{ item }">
        <div>{{ item.duration }} {{ item.duration > 0 ? " min" : "" }}</div>
      </template>
      <template v-slot:energy_consumed="{ item }">
        <div>
          <v-chip
            color="blue"
            label
          >
            {{ item.energy_consumed }}
            {{ item.energy_consumed > 0 ? " kWh" : "" }}
          </v-chip>
        </div>
      </template>
      <template v-slot:total_cost="{ item }">
        <div>
          {{
            (item.total_cost !== "N/A" && item.total_cost > 0
              ? item.total_cost
              : item.total_cost) | commaFormat
          }}
        </div>
      </template>
      <template v-slot:status="{ item }">
        <div>
          <v-chip
            :color="item.status == 'Completed' ? 'green' : 'yellow'"
            label
          >
            {{ item.status }}
          </v-chip>
        </div>
      </template>

      <template #description>
        <section class="kpi-grid">
          <v-card
            class="kpi-card"
            flat
          >
            <v-card-text>
              <div class="kpi-label">
                {{ $t("total_duration") }}
              </div>
              <div class="kpi-value">
                {{ kpiData?.total_duration }}
              </div>
              <div class="kpi-sub">
                session duration in scope
              </div>
            </v-card-text>
          </v-card>

          <v-card
            class="kpi-card"
            flat
          >
            <v-card-text>
              <div class="kpi-label">
                {{ $t("total_energy_consumed") }}
              </div>
              <div class="kpi-value">
                {{ kpiData?.total_energy_consumed }} kWh
              </div>
              <div class="kpi-sub">
                energy dispensed in scope
              </div>
            </v-card-text>
          </v-card>

          <v-card
            class="kpi-card"
            flat
          >
            <v-card-text>
              <div class="kpi-label">
                {{ $t("total_cost") | currencyFormat }}
              </div>
              <div class="kpi-value">
                {{ kpiData?.total_cost | commaFormat }}
              </div>
              <div class="kpi-sub">
                gross revenue billed to customers
              </div>
            </v-card-text>
          </v-card>
        </section>
      </template>

      <template #addFilters>
        <v-card>
          <v-select
            v-model="customFilters.status"
            filled
            dense
            clearable
            :items="statusList"
            item-value="id"
            item-text="name"
            :label="$t('status')"
            @change="fetchData"
          />
        </v-card>
      </template>
      <template #moreFilters>
        <div>
          <refresh-button
            :is-loading="isLoading"
            @reload="fetchData"
          />
        </div>
      </template>
      <template #actionModals>
        <!-- <edit-md-make v-if="editModal" :is-visible-dialog="editModal" :toggle-edit="toggleEditModal"
          :md-make="selectedRow" /> -->
        <Modal
          :is-visible="isDetail"
          :is-view="true"
          :toggle="openDetailModal"
          :title="$t('charging_session_detail')"
          width="800px"
        >
          <charging-session-detail
            v-if="selectedRow"
            :selected-row="selectedRow"
          />
        </Modal>

        <edit-customer
          v-if="editModal"
          :is-visible-dialog="editModal"
          :toggle-edit="toggleEditModal"
          :customer-data="selectedRow"
          @change="fetchData"
        />
      </template>
    </ListingLayout>
    <!-- <Modal v-if="showModal" :is-visible="showModal" is-view :toggle="() => (showModal = false)">
      <edit-customer :customer-data="customerData" />

    </Modal> -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ListingLayoutMixins } from "@/mixins/ListingLayoutMixins";

// import { debounce } from "lodash";
export default {
  components: {
    ListingLayout: () => import("@/layouts/section/ListingLayout.vue"),
    refreshButton: () =>
      import("../../../../components/base/RefreshButton.vue"),
    EditCustomer: () => import("./dialogs/EditCustomer.vue"),
    ChargingSessionDetail: () => import("./dialogs/ChargingSessionDetails.vue"),
    Modal: () => import("@/components/Modals/Modal.vue"),
  },
  mixins: [ListingLayoutMixins],
  data() {
    return {
      intervalLoading: false,

      productOptions: [
        { id: "oem", value: "OEM" },
        { id: "cso", value: "CSO" },
      ],
      filters: {},
      customFilters: {},
      statusList: [
        {
          id: "in-progress",
          name: "In-Progress",
        },
        {
          id: "completed",
          name: "Completed",
        },
      ],
      showModal: false,
      isMounted: false,
      customerData: {},
      interval: null,

      //
      isDetail: false,
      selectedRow: null,
    };
  },
  computed: {
    ...mapGetters({
      authUser: "getUser",
      isLoading: "csmsChargingSession/getLoading",
      meta: "csmsChargingSession/getMeta",
      list: "csmsChargingSession/getList",
      //
      kpiData: "csmsChargingSession/getAggregated",
    }),
    _headers() {
      const headers = [
        {
          text: this.$t("serial_no"),
          align: "left",
          sortable: false,
          value: "sno",
        },
        {
          text: this.$t("id"),
          align: "left",
          sortable: true,
          value: "id",
        },
        {
          text: this.$t("charge_point_id"),
          align: "left",
          sortable: true,
          value: "charge_point_id",
        },

        {
          text: this.$t("charging_location"),
          align: "left",
          sortable: false,
          value: "location_name",
        },
        {
          text: this.$t("city"),
          align: "left",
          sortable: false,
          value: "city_name",
        },

        {
          text: this.$t("date"),
          align: "left",
          sortable: true,
          value: "date",
        },
        {
          text: this.$t("start_time"),
          align: "left",
          sortable: true,
          value: "started_at",
        },
        {
          text: this.$t("start_soc"),
          align: "left",
          sortable: true,
          value: "start_soc",
        },
        {
          text: this.$t("end_time"),
          align: "left",
          sortable: true,
          value: "completed_at",
        },
        {
          text: this.$t("end_soc"),
          align: "left",
          sortable: true,
          value: "end_soc",
        },
        {
          text: this.$t("duration"), //min
          align: "left",
          sortable: true,
          value: "duration",
        },
        {
          text: this.$t("energy_dispensed"), //kWh
          align: "center",
          sortable: true,
          value: "energy_consumed",
        },
        {
          text: this.$t("charge_total_cost"), //currency
          align: "center",
          sortable: true,
          value: "total_cost",
        },

        {
          text: this.$t("status"),
          align: "left",
          sortable: true,
          value: "status",
        },
        {
          text: this.$t("customer_name"),
          align: "left",
          sortable: false,
          value: "customer_name",
        },
        {
          text: this.$t("make"),
          align: "left",
          sortable: false,
          value: "make",
        },
        {
          text: this.$t("model"),
          align: "left",
          sortable: false,
          value: "model",
        },
        {
          text: this.$t("vehicle"),
          align: "left",
          sortable: false,
          value: "vehicle_reg_no",
        },
      ];
      // computed headers for showing columns to role based
      return headers
        .filter(
          (header) =>
            header.showAdmin === undefined ||
            (header.showAdmin != undefined &&
              header.showAdmin === this.authUser.isAdmin)
        )
        .map((r) => ({ ...r, width: "5%" }));
    },
    currencySign() {
      return localStorage.getItem("selectedCurrency") || "";
    },
    totalDuration() {
      return this.list.reduce((sum, item) => {
        let d = item.duration.replace("m", "").replace("h", "");
        return sum + (Number(d) || 0);
      }, 0);
    },
    totalEnergyConsumed() {
      return this.list
        .reduce((sum, item) => sum + (Number(item.energy_consumed) || 0), 0)
        .toFixed(2);
    },
    totalCost() {
      return this.list
        .reduce((sum, item) => sum + (Number(item.total_cost) || 0), 0)
        .toFixed(2);
    },
  },
  watch: {
    "options.tenant_id": {
      handler: function () {},
    },

    options() {
      this.fetchData();
    },
  },
  async mounted() {
    if (this.$route?.query?.status) {
      this.customFilters.status = this.$route.query.status;
    }
  },
  beforeDestroy() {
    clearTimeout(this.interval);
    const paramsSet = { ...this.options, ...this.customFilters };
    const params = {
      name: "charging_sessions",
      filters: paramsSet,
    };
    this.$store.commit("redirect/SET_FILTERS", params);
  },
  methods: {
    async fetchData() {
      clearTimeout(this.interval);
      const params = { ...this.options, ...this.customFilters };
      await this.$store.dispatch("csmsChargingSession/list", params);

      this.interval = setTimeout(() => {
        this.fetchByInterval();
      }, 30000);
    },
    async fetchByInterval() {
      this.intervalLoading = true;
      await this.fetchData();
      this.intervalLoading = false;
    },
    openDetailModal(val) {
      if (val) {
        this.selectedRow = val;
        this.isDetail = true;
      } else {
        this.isDetail = false;
        this.selectedRow = null;
        this.$store.commit("csmsChargingSession/SET_ENERGY_DATA", null);
      }
    },

    //
    onChangeLocation(v) {
      delete this.customFilters.charge_point_id;
      if (v) {
        const params = {
          tenant_id: this.options.tenant_id,
          location_id: v,
        };
        this.$store.dispatch("csmsChargeStations/chargerList", params);
      } else {
        this.$store.commit("csmsChargeStations/SET_CHARGERS", []);
      }
      this.fetchData();
    },
    async downloadFiles(v) {
      const params = { ...this.options, ...this.customFilters };
      this.$store
        .dispatch("csmsChargingSession/downloadList", params)
        .then((r) => {
          const url = r[v];
          window.open(url, "_blank");
        });
    },
  },
};
</script>
<style lang="sass" scoped>
.kpi-grid
  display: grid
  grid-template-columns: repeat(3, 1fr)
  gap: 1rem
  margin-bottom: 1rem

.kpi-card
  border-radius: 12px !important
  border: 1px solid #f0f2f5

.kpi-label
  color: gray
  font-size: 0.875rem

.kpi-value
  font-size: 1.75rem
  font-weight: 700
  color: $theme-color
  margin-top: 0.25rem

.kpi-sub
  font-size: 0.75rem
  color: gray
  margin-top: 0.25rem

@media (max-width: 900px)
  .kpi-grid
    grid-template-columns: repeat(2, 1fr)

@media (max-width: 600px)
  .kpi-grid
    grid-template-columns: 1fr

:deep .blue
  background-color: #d2e3fc !important
:deep .green
  color: #00b894 !important
  background-color: #e3fcef !important
:deep .yellow
  color: #afa018 !important
  background-color: #fffacd !important

.map-button
  height: 41px !important
  margin-right: 5px

.btn-disabled
  pointer-events: none !important
  background-color: #8f8f8f !important

:deep .v-autocomplete.v-text-field--enclosed:not(.v-text-field--solo):not(.v-text-field--single-line):not(.v-text-field--outlined).v-input--dense .v-select__slot > input
  font-size: 0.875rem !important
</style>
